import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LoadingPage from "../components/pages/user/LoadingPage";
import { useRecoilState } from "recoil";
import {
  authState,
  bookmarksState,
  carouselState,
  categoriesState,
  categoryTypesState,
  contentState,
  freeContentState,
  interestsState,
  organisationState,
  paymentsState,
  subscriptionState,
  userPaymentsState,
  usersState,
  viewsState,
} from "../hooks/stateAtoms";
import API from "../hooks/API";
import { jwtDecode } from "jwt-decode";

// const LandingPageView = React.lazy(
//   () => import("../components/pages/user/LandingPage")
// );
const LoginPageView = React.lazy(
  () => import("../components/pages/auth/LogInPage")
);
const SignUpPageView = React.lazy(
  () => import("../components/pages/auth/SignUpPage")
);
const ForgotPasswordView = React.lazy(
  () => import("../components/pages/auth/ForgotPasswordPage")
);
const ProfileView = React.lazy(
  () => import("../components/pages/user/ProfilePage")
);
const SavedView = React.lazy(
  () => import("../components/pages/user/SavedPage")
);
const HomeView = React.lazy(() => import("../components/pages/user/HomePage"));
const UploadsView = React.lazy(
  () => import("../components/pages/user/UploadsPage")
);
const ContentDetailsView = React.lazy(
  () => import("../components/pages/user/ContentDetailsPage")
);
const ContentAdminView = React.lazy(
  () => import("../components/pages/admin/ContentPage")
);
const SettingsAdminView = React.lazy(
  () => import("../components/pages/admin/SettingsPage")
);
const LogsAdminView = React.lazy(
  () => import("../components/pages/admin/LogsPage")
);
const HomeAdminView = React.lazy(
  () => import("../components/pages/admin/HomePage")
);
const UsersAdminView = React.lazy(
  () => import("../components/pages/admin/UsersPage")
);
const UserDetailsAdminView = React.lazy(
  () => import("../components/pages/admin/UserDetailsPage")
);
const PaymentsAdminView = React.lazy(
  () => import("../components/pages/admin/PaymentsPage")
);
const PaymentsUserView = React.lazy(
  () => import("../components/pages/user/PaymentsUserPage")
);
const ConfigsAdminView = React.lazy(
  () => import("../components/pages/admin/ConfigsPage")
);
const ResetPasswordView = React.lazy(
  () => import("../components/pages/auth/ResetPasswordPage")
);
const VerifyEmailView = React.lazy(
  () => import("../components/pages/auth/VerifyEmailPage")
);

export default function Navigation() {
  const [, setCategoryList] = useRecoilState(categoriesState);
  const [, setSubscriptionList] = useRecoilState(subscriptionState);
  const [contentList, setContentList] = useRecoilState(contentState);
  const [, setUsersList] = useRecoilState(usersState);
  const [, setOrgList] = useRecoilState(organisationState);
  const [, setCategoryTypesList] = useRecoilState(categoryTypesState);
  const [, setCarouselList] = useRecoilState(carouselState);
  const [, setBookmarksList] = useRecoilState(bookmarksState);
  const [, setPaymentsList] = useRecoilState(paymentsState);
  const [, setUserPaymentsList] = useRecoilState(userPaymentsState);
  const [, setInterestsList] = useRecoilState(interestsState);
  const [, setViewsList] = useRecoilState(viewsState);
  const [, setFreeContentList] = useRecoilState(freeContentState);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [auth, setAuth] = useRecoilState(authState);
  const categoriesService = new API("categories");
  const categoryTypesService = new API("category-types");
  const contentService = new API("contents");
  const userService = new API("users");
  const orgService = new API("organisations");
  const bookmarksService = new API("bookmarks");
  const paymentsService = new API("payments");
  const interestsService = new API("user-interests");
  const subscriptionService = new API("subscriptions");
  const carouselService = new API("home_carousel");
  const viewsService = new API("views");

  useEffect(() => {
    //console.log(auth)
    // Check auth token and set default auth state if not present
    if (!auth?.token && auth?.isAuthenticated !== false) {
      setAuth({
        isAuthenticated: false,
        token: null,
        user: null,
        isAdmin: false,
      });
    }
  }, [auth?.token, auth?.isAuthenticated, setAuth]);

  useEffect(() => {
    carouselService.fetchData(null, setCarouselList);
    contentService.fetchData(null, setFreeContentList, "freemium");
    categoriesService.fetchData(null, setCategoryList);
    if (auth?.isAuthenticated) {
      let subscription = auth.user?.subscription_name;
      contentService.fetchData(null, setContentList, subscription);
      paymentsService.fetchData(auth.user?.uid, setUserPaymentsList);
      categoryTypesService.fetchData(null, setCategoryTypesList);
      subscriptionService.fetchData(null, setSubscriptionList);
      interestsService.fetchData(null, setInterestsList);
      paymentsService.fetchData(null, setPaymentsList);
      bookmarksService.fetchData(null, setBookmarksList);
      userService.fetchData(null, setUsersList);
      if (auth?.isAdmin) {
        categoryTypesService.fetchData(null, setCategoryTypesList);
        orgService.fetchData(null, setOrgList);
        contentService.fetchData(null, setContentList);
        viewsService.fetchData(null, setViewsList);
      }
    }
  }, [auth]);

  useEffect(() => {
    if (contentList.length > 0 && auth.isAuthenticated) {
      let subscription = auth.user?.subscription_name;
      if (subscription?.toLowerCase() === "freemium") {
        setContentList(
          contentList.filter(
            (content: any) =>
              content.premium === false || content.premium === null
          )
        );
      }
    }
  }, [auth]);

  useEffect(() => {
    if (auth?.isAuthenticated) {
      let token = auth?.token;
      if (!token) {
        setAuth({
          isAuthenticated: false,
          token: null,
          user: null,
          isAdmin: false,
        });
      } else {
        const decoded: any = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          setAuth({
            isAuthenticated: false,
            token: null,
            user: null,
            isAdmin: false,
          });
        }
      }
    }
  }, []);

  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        {/* Session path */}
        <Route
          path="/"
          element={
            auth?.isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <LoginPageView />
            )
          }
        />

        <Route
          path="/login"
          element={
            auth?.isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <LoginPageView />
            )
          }
        />

        <Route path="/forgot" element={<ForgotPasswordView />} />

        <Route path="/reset-password/:token" element={<ResetPasswordView />} />
 
        <Route path="/verify-email/:token" element={<VerifyEmailView />} />

        <Route
          path="/signup"
          element={
            auth?.isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <SignUpPageView />
            )
          }
        />

        <Route
          path="/saved"
          element={
            auth?.isAuthenticated ? (
              <SavedView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/content/:id"
          element={
            auth?.isAuthenticated ? (
              <ContentDetailsView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/uploads"
          element={
            auth?.isAuthenticated ? (
              <UploadsView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/home"
          element={
            auth?.isAuthenticated ? (
              auth?.isAdmin ? (
                <HomeAdminView />
              ) : (
                <HomeView />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/profile"
          element={
            auth?.isAuthenticated ? (
              <ProfileView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/content"
          element={
            auth?.isAuthenticated ? (
              <ContentAdminView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/history"
          element={
            auth?.isAuthenticated ? (
              <LogsAdminView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/payments"
          element={
            auth?.isAuthenticated ? (
              auth?.isAdmin ? (
                <PaymentsAdminView />
              ) : (
                <PaymentsUserView />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* <Route
          path="/userpayments"
          element={
            auth?.isAuthenticated ? (
              <PaymentsUserView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        /> */}

        <Route
          path="/configs"
          element={
            auth?.isAuthenticated && auth?.isAdmin ? (
              <ConfigsAdminView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/users"
          element={
            auth?.isAuthenticated && auth?.isAdmin ? (
              <UsersAdminView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/users/:id"
          element={
            auth?.isAuthenticated ? (
              <UserDetailsAdminView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/settings"
          element={
            auth?.isAuthenticated && auth?.isAdmin ? (
              <SettingsAdminView />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Suspense>
  );
}
